import { useMemo } from "react";

const STEPS = [
  { id: "inputs",  num: 1, label: "Inputs",  hint: "Content we need to fulfill your request." },
  { id: "content", num: 2, label: "Content", hint: "Review the copy and assets we assembled." },
  { id: "design",  num: 3, label: "Design",  hint: "Review the rendered layout." },
];

/**
 * Three-step workflow header. `current` is the active step the marketer is on;
 * `furthest` is the furthest step they've reached (steps before that are
 * navigable; steps after are gated). `onJump` is called when the marketer
 * clicks a navigable step.
 */
export default function WorkflowStepper({ current = "inputs", furthest = "inputs", onJump }) {
  const order = useMemo(() => STEPS.map((s) => s.id), []);
  const idxFurthest = order.indexOf(furthest);

  return (
    <nav
      data-testid="workflow-stepper"
      aria-label="Workflow steps"
      className="mt-6 flex flex-wrap gap-3"
    >
      {STEPS.map((s) => {
        const idx = order.indexOf(s.id);
        const isCurrent = s.id === current;
        const isReachable = idx <= idxFurthest;
        return (
          <button
            key={s.id}
            type="button"
            data-testid={`workflow-step-${s.id}`}
            disabled={!isReachable}
            onClick={() => isReachable && onJump?.(s.id)}
            className={[
              "flex-1 min-w-[200px] text-left px-4 py-3 rounded-md border transition-colors",
              isCurrent
                ? "bg-[#1A1F2C] text-[#F8F5EE] border-[#1A1F2C]"
                : isReachable
                ? "bg-white text-[#3D3A35] border-[#1A1F2C]/20 hover:border-[#1A1F2C]/40"
                : "bg-[#F8F5EE]/50 text-[#6B6358] border-[#1A1F2C]/10 cursor-not-allowed opacity-70",
            ].join(" ")}
          >
            <div className="flex items-center gap-2">
              <span
                className={[
                  "inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-medium",
                  isCurrent ? "bg-[#F8F5EE] text-[#1A1F2C]" : "bg-[#1A1F2C]/10 text-[#1A1F2C]",
                ].join(" ")}
              >
                {s.num}
              </span>
              <span className="text-base font-medium">{s.label}</span>
            </div>
            <p className={`text-xs mt-1 ${isCurrent ? "text-[#F8F5EE]/80" : "text-[#6B6358]"}`}>
              {s.hint}
            </p>
          </button>
        );
      })}
    </nav>
  );
}

export { STEPS as WORKFLOW_STEPS };
